import React from "react";
import Button from "@mui/material/Button";
import ExcelJS from "exceljs";
import ButtonComponent from "../customButton";
import { useSelector, useDispatch } from "react-redux";
import { showSnackbar } from "../../features/snakbar/snackbarSlice";
import { fetchModulesWithCount } from "../../utils/api";

import { dropdownValidation, positiveIntegerValidation, TYPE_DROPDOWN_VALUES, STATUS_DROPDOWN_VALUES, PRIORITY_DROPDOWN_VALUES, TOTAL_ROWS, FORMULA_ROWS, max200CharValidation } from "../../utils/excelExportHelperFunction";
import { randomAlphaNumeric } from "../../utils/commonfunctions";

// Helper function to convert module tree data to path mapping
function getAllModuleCombinations(data) {
  const paths = {};

  function traverse(node, pathSoFar) {
    const newPath = [...pathSoFar, node.name];
    const pathString = newPath.join('/');
    paths[pathString] = node.id;

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child, newPath));
    }
  }

  data.forEach(root => traverse(root, []));
  return paths;
}

const ExcelExportExampleButton = ({ modulePaths , labelOptions }) => {
  const { selectedProject } = useSelector((state) => state.projects);
  const dispatch = useDispatch();

  async function handleSheetDownload() {
    try {
      // Validate that a project is selected
      if (!selectedProject || !selectedProject.id) {
        dispatch(showSnackbar({ 
          message: "No project selected. Please select a project before exporting the template.", 
          severity: "error",
          duration: 5000 
        }));
        return;
      }

      // Fetch the latest modules to ensure they are up to date
      let freshModulePaths = modulePaths;
      try {
        const modulesData = await fetchModulesWithCount();
        if (Array.isArray(modulesData) && modulesData.length > 0) {
          freshModulePaths = getAllModuleCombinations(modulesData);
        } else if (!modulePaths || Object.keys(modulePaths).length === 0) {
          dispatch(showSnackbar({ 
            message: "No modules available. Please ensure modules are loaded before exporting the template.", 
            severity: "error",
            duration: 5000 
          }));
          return;
        }
      } catch (error) {
        console.error("Error fetching modules:", error);
        // Fallback to prop modulePaths if fetch fails
        if (!modulePaths || Object.keys(modulePaths).length === 0) {
          dispatch(showSnackbar({ 
            message: "Error fetching modules. Please try again.", 
            severity: "error",
            duration: 5000 
          }));
          return;
        }
      }

      // Validate that modules are present after fetch
      if (!freshModulePaths || Object.keys(freshModulePaths).length === 0) {
        dispatch(showSnackbar({ 
          message: "No modules available. Please ensure modules are loaded before exporting the template.", 
          severity: "error",
          duration: 5000 
        }));
        return;
      }
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");

     

      const headers = [
        "Module", "title", "description", "type",
        "status", "priority",
        "estimated_duration", 
        "labels"
      ];
      worksheet.addRow(headers);

      for (let i = 0; i < TOTAL_ROWS; i++) {
        worksheet.addRow(new Array(headers.length).fill(""));
      }

      // Add dropdown options and mapping to a hidden sheet
      const dropdownSheet = workbook.addWorksheet("DropdownOptions");
      const moduleKeys = Object.keys(freshModulePaths);

      // Column A: visible names, Column B: actual values
      moduleKeys.forEach((key, idx) => {
        dropdownSheet.getCell(`A${idx + 1}`).value = key;
        dropdownSheet.getCell(`B${idx + 1}`).value = freshModulePaths[key];
      });
      dropdownSheet.state = "veryHidden";

      const labelSheet = workbook.addWorksheet("LabelOptions");

      if (labelOptions && labelOptions.length > 0) {
        labelOptions.forEach((label, idx) => {
          labelSheet.getCell(`A${idx + 1}`).value = label.name;
          labelSheet.getCell(`B${idx + 1}`).value = label.id;
        });
      } else {
        labelSheet.getCell("A1").value = "No Labels";
        labelSheet.getCell("B1").value = "";
      }
 
      labelSheet.state = "veryHidden";

      const jsonLikeValidation = (row) => ({
        type: "custom",
        formulae: [
          `AND(LEFT(I${row},1)="{",RIGHT(I${row},1)="}",ISNUMBER(SEARCH(":",I${row})))`
        ],
        allowBlank: false,
        showInputMessage: true,
        promptTitle: "Enter JSON",
        prompt: "Should start with '{', end with '}', and contain ':'.",
        showErrorMessage: true,
        errorTitle: "Invalid input",
        error: "Enter a value that looks like JSON (e.g. {\"key\":\"value\"}).",
      });

      const metaSheet = workbook.addWorksheet("MetaInfo");
      metaSheet.getCell("A1").value = "Generated by";
      metaSheet.getCell("B1").value = "AP-TEST-CASE-IMPORT";
      metaSheet.getCell("A2").value = "Project ID";
      metaSheet.getCell("B2").value = selectedProject?.id || null;
      metaSheet.state = "veryHidden";

      // Apply validations only to first 50 editable rows
      const moduleRange = `DropdownOptions!$A$1:$A$${moduleKeys.length}`;
      const totalLabelRows = (labelOptions && labelOptions.length > 0) ? labelOptions.length : 1;
      const labelRange = `LabelOptions!$A$1:$A$${totalLabelRows}`;

      for (let row = 2; row < 2 + FORMULA_ROWS; row++) {
        worksheet.getCell(`A${row}`).dataValidation = dropdownValidation([moduleRange]);
        worksheet.getCell(`B${row}`).dataValidation = max200CharValidation;
        worksheet.getCell(`D${row}`).dataValidation = dropdownValidation(TYPE_DROPDOWN_VALUES);
        worksheet.getCell(`E${row}`).dataValidation = dropdownValidation(STATUS_DROPDOWN_VALUES);
        worksheet.getCell(`F${row}`).dataValidation = dropdownValidation(PRIORITY_DROPDOWN_VALUES);
        worksheet.getCell(`G${row}`).dataValidation = positiveIntegerValidation;
        worksheet.getCell(`H${row}`).dataValidation = dropdownValidation([labelRange], false);
      }  

      // Add a note to the header for Module column to clarify mapping
      worksheet.getCell("A1").note =
        "Select a module path from the dropdown. On import, the corresponding module ID will be used. " +
        "The dropdown shows the module path (name), but the backend will use the mapped ID. " +
        "If you want to import by ID directly, you can paste the ID instead of the name.";

      worksheet.getCell("H1").note =
        "Labels (comma-separated if multiple)..";

      // First protect worksheet (default all cells locked)
      const password = randomAlphaNumeric(56);
      await worksheet.protect(password, {
        selectLockedCells: true,
        selectUnlockedCells: true,
        formatCells: false,
        formatColumns: true,
        formatRows: false,
        insertColumns: false,
        insertRows: false,
        insertHyperlinks: false,
        deleteColumns: false,
        deleteRows: false,
        sort: false,
        autoFilter: false,
        pivotTables: false,
      });

      // Unlock all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell(cell => {
          cell.protection = { locked: false };
        });
      });

      // Re-lock only the first row (headers)
      worksheet.getRow(1).eachCell(cell => {
        cell.protection = { locked: true };
      });

      worksheet.columns.forEach((column, i) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const value = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, value.length);
        });

        column.width = maxLength < 10 ? 10 : maxLength + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "TestCaseImportTemplate.xlsx";
      link.click();
    } catch (error) {
      console.error("Error downloading sheet:", error);
    }
  }

  return (
    <ButtonComponent
      variant="primary"
      className="white bg-blue600"
      onClick={handleSheetDownload}
      ctaText={"Export Example Excel"}
    />
  );
};

export default ExcelExportExampleButton;
